import {
  ADMIN_TRADING_CONFIGURATION_FORM_PATH,
  BASE_BOTS_BACKTESTING_PATH,
  BASE_BOTS_WITH_ACTION_PATH,
  BASE_PUBLIC_BOT_PATH,
  DCA_PUBLIC_BOTS_PATH,
} from "common/constants.ts";
import { useRouteMatch } from "react-router-dom";
import {
  BotConfigurationType,
  BotType,
  LanguageNameSpaces,
  QueryBotType,
} from "@shared/types";
import { capitalizeFirstLetter } from "@shared/common/utils.ts";
import useQuery from "utils/hooks/useQuery.ts";
import { QUERY_BOT_TYPE_TO_BOT_TYPE } from "@shared/common/constants.ts";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import { joinWords } from "@shared/common/i18n/utils.ts";

const useBotsPageTitleString = (): string => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const match = useRouteMatch(BASE_BOTS_WITH_ACTION_PATH);
  const isBacktesting = !!useRouteMatch(BASE_BOTS_BACKTESTING_PATH);
  const { botType: queryBotType, action: queryAction } = (match?.params ||
    {}) as {
    botType?: QueryBotType;
    action?: string;
  };
  const publicBotMatch = useRouteMatch(BASE_PUBLIC_BOT_PATH);
  const publicBotsMatch = useRouteMatch(DCA_PUBLIC_BOTS_PATH);
  const { botType: publicBotQueryBotType } = (match?.params || {}) as {
    botType?: QueryBotType;
  };
  const channelConfigurationsMatch = useRouteMatch<{
    configurationType?: BotConfigurationType;
  }>(ADMIN_TRADING_CONFIGURATION_FORM_PATH);
  const { configurationType } = channelConfigurationsMatch?.params || {};
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const query = useQuery();
  const botIds = query.get("botIds");
  const botType =
    QUERY_BOT_TYPE_TO_BOT_TYPE[
      queryBotType || publicBotQueryBotType || QueryBotType.ASAP
    ];
  const action = queryAction;
  const isBulkCreate = action === "bulk_create";

  const getActionDisplay = () => {
    if (isBacktesting) return;
    if (action === "my-bots" || !action) return t("page_title.bots.my");
    if (publicBotMatch) return t("page_title.bots.copy");
    if (isBulkCreate) return t("page_title.bots.bulk_create");
    const actionKey = BOT_FORM_ACTION_TO_DISPLAY_NAME.get(action);
    return actionKey ? t(actionKey) : capitalizeFirstLetter(action);
  };

  let botTypeDisplay =
    isBottomNavigator && botType === BotType.TRADING_VIEW_SIGNALS && !action
      ? BOT_TYPE_TO_BOTS_DISPLAY_NAME[BotType.TRADING_VIEW]
      : BOT_TYPE_TO_BOTS_DISPLAY_NAME[botType];
  if (
    botType === BotType.TRADING_VIEW &&
    isBottomNavigator &&
    (botIds || isBulkCreate)
  )
    botTypeDisplay = "page_title.bots.dynamic_trading_view_bots_short";
  const actionDisplay = getActionDisplay();

  if (channelConfigurationsMatch) {
    return configurationType === BotConfigurationType.SPOT
      ? t("page_title.bots.spot_configuration")
      : t("page_title.bots.leverage_configuration");
  } else if (isBottomNavigator && !action) {
    return t(botTypeDisplay, {
      botOrBots: t("page_title.bots.bots"),
      backtesting: "",
    });
  } else if (publicBotsMatch) {
    return t("page_title.bots.public_dca_bots");
  }

  const isMultipleBots = !actionDisplay || !action || botIds || isBulkCreate;
  return joinWords([
    botIds ? t("page_title.bots.bulk") : "",
    actionDisplay,
    t(botTypeDisplay, {
      botOrBots: isMultipleBots
        ? t("page_title.bots.bots")
        : t("page_title.bots.bot"),
      backtesting: isBacktesting
        ? t("page_title.bots.dynamic_backtesting")
        : "",
    }),
  ]);
};

export default useBotsPageTitleString;

const BOT_TYPE_TO_BOTS_DISPLAY_NAME: Record<
  BotType,
  TranslationKeys<LanguageNameSpaces.COMMON>
> = {
  [BotType.ASAP]: "page_title.bots.dynamic_dca_bots",
  [BotType.TRADING_VIEW]: "page_title.bots.dynamic_trading_view_bots",
  [BotType.SIGNAL]: "page_title.bots.dynamic_signals_bots",
  [BotType.GRID]: "page_title.bots.dynamic_grid_bots",
  [BotType.TRADING_VIEW_SIGNALS]:
    "page_title.bots.dynamic_trading_view_signals_bots",
  [BotType.HEDGING]: "page_title.bots.dynamic_hedging_bots",
};
const BOT_FORM_ACTION_TO_DISPLAY_NAME = new Map<
  string,
  TranslationKeys<LanguageNameSpaces.COMMON>
>([
  ["create", "page_title.bots.create"],
  ["edit", "page_title.bots.edit"],
  ["duplicate", "page_title.bots.duplicate"],
]);
